// ============================================================================
// EcgFindingDetails.tsx — детали находок MI/HYP из ответа анализатора.
//
// Показываются ТОЛЬКО поля, реально пришедшие в result_json (см.
// plans/coord/report-ecg-mi-hyp.md, ecg-analyzer/app/model/predict.py):
//   • фаза двухэтапной оценки MI (скрининг/подтверждение) и пояснение;
//   • компактная строка ST-детектора (элевация/депрессия по отведениям);
//   • амплитудные критерии гипертрофии и источник (модель/правила/конфликт);
//   • замечание о расхождении модели и правил.
// Отсутствующее поле не выводится и не подменяется «значением по умолчанию».
// Тексты — в i18n (lib/i18n/messages.ts, ключи ecg.*).
// ============================================================================

import type { CSSProperties } from 'react';
import type { EcgFinding, EcgHypCriteria, EcgStCriteria } from '@/lib/ecgAnalysis';
import { useI18n } from '@/lib/i18n/I18nProvider';
import type { I18nContextValue } from '@/lib/i18n/I18nProvider';

const LEAD_ORDER = ['V1', 'V2', 'V3', 'V4', 'V5', 'V6', 'I', 'II', 'III', 'aVR', 'aVL', 'aVF'];

/** Порядок критериев гипертрофии (совпадает с criteria_labels анализатора). */
const HYP_CRITERIA: Array<{ code: string; key: string }> = [
    { code: 'sokolow', key: 'ecg.hyp.sokolow' },
    { code: 'cornell', key: 'ecg.hyp.cornell' },
    { code: 'peguero', key: 'ecg.hyp.peguero' },
    { code: 'rvh', key: 'ecg.hyp.rvh' },
];

type Translate = I18nContextValue['t'];

function orderedLeads(leads: string[]): string[] {
    return [...new Set(leads)].sort((a, b) => {
        const ai = LEAD_ORDER.indexOf(a);
        const bi = LEAD_ORDER.indexOf(b);
        return (ai < 0 ? LEAD_ORDER.length : ai) - (bi < 0 ? LEAD_ORDER.length : bi);
    });
}

/** Смежные отведения V2/V3/V4 → «V2–V4»; остальные — через запятую. */
function leadSpan(leads: string[]): string {
    const ordered = orderedLeads(leads);
    if (ordered.length <= 1) return ordered.join('');
    const parts = ordered.map((lead) => /^([A-Za-z]+)(\d+)$/.exec(lead));
    if (parts.every((part) => part !== null)) {
        const prefix = parts[0]?.[1] ?? '';
        const numbers = parts.map((part) => Number(part?.[2]));
        const samePrefix = parts.every((part) => part?.[1] === prefix);
        const consecutive = numbers.every((value, index) => index === 0 || value === numbers[index - 1] + 1);
        if (samePrefix && consecutive) {
            return `${prefix}${numbers[0]}–${prefix}${numbers[numbers.length - 1]}`;
        }
    }
    return ordered.join(', ');
}

function fmtMv(value: number): string {
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return `${sign}${Math.abs(value).toFixed(2)}`;
}

function extremeLeadValue(st: EcgStCriteria, leads: string[], mode: 'max' | 'min'): number | null {
    const values = leads
        .map((lead) => st.valuesMv[lead])
        .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    if (values.length === 0) return null;
    return mode === 'max' ? Math.max(...values) : Math.min(...values);
}

/** Компактная строка ST: «ST: элевация V2–V4 (+0.25 мВ) · депрессия …». */
function stSummary(st: EcgStCriteria, t: Translate): string {
    const parts: string[] = [];
    if (st.elevationLeads.length > 0) {
        const value = extremeLeadValue(st, st.elevationLeads, 'max');
        parts.push(
            `${t('ecg.st.elevation')} ${leadSpan(st.elevationLeads)}`
            + (value === null ? '' : ` (${fmtMv(value)} ${t('ecg.st.mv')})`),
        );
    }
    if (st.depressionLeads.length > 0) {
        const value = extremeLeadValue(st, st.depressionLeads, 'min');
        parts.push(
            `${t('ecg.st.depression')} ${leadSpan(st.depressionLeads)}`
            + (value === null ? '' : ` (${fmtMv(value)} ${t('ecg.st.mv')})`),
        );
    }
    if (parts.length > 0) return `${t('ecg.st.prefix')} ${parts.join(' · ')}`;
    if (!st.evaluated) {
        return st.reason ? `${t('ecg.st.unavailable')}: ${st.reason}` : '';
    }
    return t('ecg.st.none');
}

/** «Sokolow-Lyon+ / Cornell− / Peguero-Lo Presti+» — только присутствующие критерии. */
function hypCriteriaLine(criteria: EcgHypCriteria, t: Translate): string {
    return HYP_CRITERIA
        .filter(({ code }) => criteria.results[code])
        .map(({ code, key }) => `${t(key)}${criteria.results[code].passed ? '+' : '−'}`)
        .join(' / ');
}

function hypSourceLabel(source: string, t: Translate): string {
    if (!source) return '';
    const key = `ecg.hyp.source.${source}`;
    const label = t(key);
    return label === key ? source : label;
}

/** Бейдж по СЛОТУ темы (цвет — CSS-переменная, тема пользователя). */
function badgeStyle(color: string): CSSProperties {
    return {
        color,
        borderColor: `color-mix(in srgb, ${color} 55%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
    };
}

/** Баннер состояния блока mi_hyp (только реальный статус `abstain`). */
export function EcgMiHypStatusBanner({ status, reason }: { status: string; reason: string }) {
    const { t } = useI18n();
    if (status !== 'abstain') return null;
    return (
        <div className="rounded-md border px-3 py-2 space-y-0.5" style={badgeStyle('var(--color-destructive)')}>
            <div className="text-xs font-medium">{t('ecg.mi_hyp.abstain')}</div>
            {reason && <div className="text-[11px] text-muted-foreground">{reason}</div>}
        </div>
    );
}

/** Детали одной находки; для находок без MI/HYP-полей ничего не рисует. */
export function EcgFindingDetails({ finding }: { finding: EcgFinding }) {
    const { t } = useI18n();
    const phase = finding.phase;
    const isHyp = !!finding.hypCriteria || !!finding.hypSource;
    const stLine = finding.st ? stSummary(finding.st, t) : '';
    if (!phase && !stLine && !isHyp && !finding.conflictNote) return null;

    const hypLine = finding.hypCriteria ? hypCriteriaLine(finding.hypCriteria, t) : '';
    const source = hypSourceLabel(finding.hypSource, t);
    const conflict = (isHyp && finding.hypSource === 'conflict') || !!finding.conflictNote;
    const miNote = phase
        ? finding.confidenceNote
            || t(phase === 'confirmed' ? 'ecg.mi.text.confirmed' : 'ecg.mi.text.screening')
        : '';
    const hypStatic = t('ecg.hyp.text');
    const hypNote = finding.confidenceNote && finding.confidenceNote.trim() !== hypStatic
        ? finding.confidenceNote
        : '';

    return (
        <div className="space-y-1 pt-1">
            {phase && (
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        className="text-[11px] font-medium border rounded-md px-2 py-0.5"
                        style={badgeStyle(phase === 'confirmed' ? 'var(--color-destructive)' : 'var(--color-yellow)')}
                        title={t(phase === 'confirmed' ? 'ecg.mi.text.confirmed' : 'ecg.mi.text.screening')}
                    >
                        {t(phase === 'confirmed' ? 'ecg.mi.badge.confirmed' : 'ecg.mi.badge.screening')}
                    </span>
                </div>
            )}
            {miNote && <div className="text-[11px] text-muted-foreground">{miNote}</div>}
            {stLine && <div className="text-xs">{stLine}</div>}
            {isHyp && (
                <div className="space-y-0.5">
                    <div className="text-xs">
                        <span className="text-muted-foreground">{t('ecg.hyp.title')}: </span>
                        {hypLine || t('ecg.hyp.no_criteria')}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{hypStatic}</div>
                    {source && (
                        <div className="text-[11px] text-muted-foreground">
                            {t('ecg.hyp.source_label', { source })}
                        </div>
                    )}
                    {hypNote && <div className="text-[11px] text-muted-foreground">{hypNote}</div>}
                </div>
            )}
            {conflict && (
                <div className="text-[11px]" style={{ color: 'var(--color-destructive)' }}>
                    {t('ecg.conflict')}
                </div>
            )}
            {finding.conflictNote && (
                <div className="text-[11px] text-muted-foreground">{finding.conflictNote}</div>
            )}
            {!phase && finding.confidenceNote && !isHyp && (
                <div className="text-[11px] text-muted-foreground">{finding.confidenceNote}</div>
            )}
        </div>
    );
}
